import { ArrowRight, Lock, ShieldCheck, History, Server } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Chapter, FadeUp } from "@/components/Reveal";

const FEATURES = [
  {
    num: "01",
    title: "Gestion Multi-dossiers & Partitionnement",
    text: "Ouvrez autant de Data Rooms étanches que nécessaire. Chaque dossier bénéficie de sa propre arborescence sécurisée, évitant tout risque d'interférence ou de fuite d'informations entre vos filiales, cessions ou procédures.",
  },
  {
    num: "02",
    title: "Dépôt FEC & Restitution Instantanée",
    text: "Déposez votre Fichier d'Écritures Comptables (FEC) et vos pièces brutes en quelques clics. L'infrastructure ACS Corporate traite et modélise vos données pour générer des analyses financières exploitables.",
  },
  {
    num: "03",
    title: "Chatroom Documentée & Rôles Sur-Mesure",
    text: "Échangez directement au cœur du dossier. Invitez vos collaborateurs, avocats, experts-comptables ou mandataires judiciaires en attribuant des droits d'accès fins (consultation seule, annotation, téléchargement).",
  },
];

const SECURITY = [
  {
    icon: Lock,
    title: "Chiffrement AES-256",
    text: "L'intégralité de vos fichiers et données au repos et en transit est cryptée selon les normes bancaires.",
  },
  {
    icon: ShieldCheck,
    title: "Secret Professionnel",
    text: "Nos processus technologiques s'articulent avec la rigueur requise pour préserver le secret des affaires et les exigences des procédures amiables ou judiciaires.",
  },
  {
    icon: History,
    title: "Traçabilité & Horodatage",
    text: "Chaque action, dépôt de document ou consultation fait l'objet d'un journal d'audit infalsifiable.",
  },
  {
    icon: Server,
    title: "Hébergement Souverain",
    text: "Vos serveurs sont localisés exclusivement en France et dans l'Union Européenne, garantissant une étanchéité totale face au Cloud Act.",
  },
];

export default function DataRoom() {
  return (
    <div className="bg-slate-50 min-h-screen flex flex-col" data-testid="dataroom-page">
      <Navbar />
      <main className="flex-1">
        <section
          className="relative w-full min-h-fit md:min-h-[70vh] flex items-center overflow-hidden"
          data-testid="dataroom-hero"
        >
          <img
            src="/data-room.png"
            alt="Data Room ACS Corporate — serveurs sécurisés"
            className="absolute inset-0 w-full h-full object-cover z-0"
            data-testid="dataroom-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-transparent z-10" />
          <div className="relative z-20 w-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 md:py-32">
            <FadeUp>
              <Chapter index="01" label="Data Room Sécurisée" />
            </FadeUp>
            <FadeUp delay={0.1}>
              <h1
                className="mt-8 max-w-3xl font-sans font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
                data-testid="dataroom-title"
              >
                Data Room ACS Corporate : Haute Sécurité et Clarté Décisionnelle
              </h1>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p
                className="mt-8 max-w-2xl text-base sm:text-lg text-slate-200 leading-relaxed"
                data-testid="dataroom-subtitle"
              >
                Centralisez vos dossiers sensibles, collaborez en temps réel et consultez des
                analyses financières à haute valeur ajoutée dans un environnement 100 % souverain.
              </p>
            </FadeUp>
          </div>
        </section>

        <section className="bg-slate-50" data-testid="dataroom-features">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 sm:py-28 lg:py-32">
            <FadeUp>
              <Chapter index="02" label="La plateforme" />
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-8 max-w-3xl font-sans font-extrabold text-slate-900 text-3xl sm:text-4xl leading-[1.15] tracking-tight">
                Une plateforme conçue pour les dossiers à fort enjeu
              </h2>
            </FadeUp>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f, i) => (
                <FadeUp key={f.num} delay={0.08 * i}>
                  <article
                    data-testid={`dataroom-feature-${i}`}
                    className="bg-white border border-gray-200 rounded-2xl overflow-hidden h-full shadow-md hover:border-slate-400 hover:shadow-xl transition-all duration-500"
                  >
                    <div
                      className="h-40 w-full border-b border-dashed border-gray-300 bg-slate-100 flex items-center justify-center"
                      data-testid={`dataroom-illustration-${i}`}
                    >
                      <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
                        Illustration {f.num} — à venir
                      </span>
                    </div>
                    <div className="p-7">
                      <span className="font-mono text-xs text-slate-400">{f.num}.</span>
                      <h3 className="mt-3 font-sans text-xl font-bold text-slate-900 tracking-tight">
                        {f.title}
                      </h3>
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">{f.text}</p>
                    </div>
                  </article>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-900" data-testid="dataroom-security">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 sm:py-28 lg:py-32">
            <FadeUp>
              <Chapter index="03" label="Sécurité & Conformité" />
            </FadeUp>
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
              <FadeUp className="lg:col-span-5">
                <h2 className="font-sans font-extrabold text-white text-3xl sm:text-4xl leading-[1.15] tracking-tight">
                  Sécurité &amp; Conformité de Niveau Bancaire
                </h2>
              </FadeUp>
              <FadeUp delay={0.15} className="lg:col-span-7">
                <p className="w-full break-words whitespace-normal text-base sm:text-lg text-slate-300 leading-relaxed">
                  <span className="font-semibold text-white">Notre Engagement :</span> la
                  confidentialité absolue n'est pas une option, c'est le socle de notre relation.
                  Vos données d'affaires bénéficient des meilleurs standards de protection
                  européens.
                </p>
              </FadeUp>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SECURITY.map((s, i) => (
                <FadeUp key={s.title} delay={0.08 * i}>
                  <div
                    data-testid={`dataroom-security-${i}`}
                    className="group bg-slate-800/50 border border-slate-700 rounded-2xl p-6 h-full transition-colors duration-500 hover:bg-slate-800"
                  >
                    <s.icon
                      strokeWidth={1.25}
                      className="w-8 h-8 text-white transition-transform duration-500 group-hover:-translate-y-1"
                    />
                    <h3 className="mt-6 font-sans font-bold text-white text-lg">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <a
                href="/#contact"
                data-testid="dataroom-cta"
                className="group mt-12 inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-medium hover:bg-slate-200 transition-colors duration-300"
              >
                Confier un dossier
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
